import React, { useEffect, useState, useRef } from 'react';
import { hintsService } from './hintsService';
import HintItem from './HintItem';
import { ELSI_STATIC_URL } from '../../const/const';

const HintsList = ({ CustomRouter, isActive, setIsActive, setRestoreHints, setIsLoading }) => {
    const [hintsList, setHintsList] = useState([]);
    const [searchHistory, setSearchHistory] = useState([]);
    const [globalSearchHistory, setGlobalSearchHistory] = useState([]);
    const [loading, setLoading] = useState(true); // Стейт загрузки
    const [isReady, setIsReady] = useState(false); // Флаг готовности компонента
    const [validImageByKey, setValidImageByKey] = useState({}); // Валидность реальных ссылок на картинки
    const hintsCache = useRef([]); // Кэш подсказок
    const listRef = useRef();

    const resetScroll = () => {
        listRef.current && listRef.current.scrollTo(0, 0);
    };

    useEffect(() => {
        const fetchData = async () => {
            // Получаем локальную историю
            setSearchHistory(hintsService.getSearchHistory());
            
            // Запускаем асинхронную загрузку глобальной истории
            hintsService.fetchGlobalSearchHistory();
        };

        fetchData();

        // Подписка на обновление подсказок
        hintsService.registerHintsObserver((hints) => {
            setHintsList(hints);
            hintsCache.current = hints;
        });

        // Callback для обновления глобальной истории после её загрузки
        const globalHistoryObserver = (globalHistory) => {
            setGlobalSearchHistory(globalHistory);
            setIsReady(true); // Флаг, что данные готовы
            if (setIsLoading) {
                setIsLoading(false); // Уведомляем родительский компонент о завершении загрузки
            }
        };

        // Подписываемся на обновление глобальной истории
        hintsService.registerGlobalHistoryObserver(globalHistoryObserver);

        return () => {
            hintsService.unRegisterHintsObserver(setHintsList);
            hintsService.unRegisterGlobalHistoryObserver(globalHistoryObserver);
        };
    }, []);

    useEffect(() => {
        if (typeof setRestoreHints === "function") {
            setRestoreHints(() => () => setHintsList(hintsCache.current));
        }
    }, [setRestoreHints]);

    // Валидация ссылок на картинки: если бэк "врёт" (битая ссылка), не подставляем реальную картинку.
    // Колонка изображения всегда отрисована с заглушкой, так что "прыжка" верстки не будет.
    useEffect(() => {
        let isCancelled = false;

        const candidates = (hintsList || [])
            .slice(0, 4)
            .filter((item) => Boolean(item && item.hasImage && item.imageName && item.productId));

        // Сбрасываем состояние при новом списке: по умолчанию у всех будет заглушка
        setValidImageByKey({});

        if (candidates.length === 0) return () => { isCancelled = true; };

        const checkImageUrl = (url) => new Promise((resolve) => {
            try {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = url;
            } catch (e) {
                resolve(false);
            }
        });

        Promise.all(candidates.map((item) => {
            const key = `${item.productId}|${item.imageName}`;
            const url = `${ELSI_STATIC_URL}/${item.productId}/${item.imageName}`;
            return checkImageUrl(url).then((isOk) => ({ key, isOk }));
        })).then((results) => {
            if (isCancelled) return;
            const next = {};
            results.forEach(({ key, isOk }) => {
                next[key] = isOk;
            });
            setValidImageByKey(next);
        });

        return () => {
            isCancelled = true;
        };
    }, [hintsList]);

    const currentSearchValue = hintsService.getSearchValue();

    const handleListClick = (e) => {
        e.stopPropagation();
        setIsActive(true);
    };

    const handleListMouseDown = (e) => {
        e.preventDefault();
    };

    // Проверяем, завершена ли загрузка и данные готовы
    if (!isReady) {
        return null;  // Не отображаем ничего во время загрузки
    }

    return (
        <ul
            className={`search-options__list chapche-event-scroll ${isActive ? "search-options__list_active" : ""}`}
            ref={listRef}
            onClick={handleListClick}
            onMouseDown={handleListMouseDown}
            tabIndex="-1"
        >
            <li className="search-options__close">
                <button
                    type="button"
                    className="search-options__close-button"
                    aria-label="Close"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsActive(false);
                    }}
                >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.00001L11 11" stroke="#1C1C1C" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M11 1.00001L1 11" stroke="#1C1C1C" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </li>

            {currentSearchValue.length >= 3 && hintsList.length > 0 && (
                <>
                    {/*
                      Чтобы исключить "прыжок" верстки:
                      - колонка изображения всегда есть у всех подсказок
                      - по умолчанию показываем заглушку
                      - реальную картинку подставляем только если ссылка успешно загрузилась (проверка в фоне)
                    */}
                    {hintsList.map((item, index) => (
                        (() => {
                            const key = (item && item.productId && item.imageName) ? `${item.productId}|${item.imageName}` : `idx|${index}`;
                            const isValid = validImageByKey[key] === true;
                            const imageSrc = (isValid && item && item.productId && item.imageName)
                                ? `${ELSI_STATIC_URL}/${item.productId}/${item.imageName}`
                                : 'http://cdn.auroraelec.ru/static/250px.svg';

                            return (
                                <HintItem
                                    key={item.productId + index}
                                    productId={item.productId}
                                    productPartNumber={item.productPartNumber}
                                    productManufacturerId={item.productManufacturerId}
                                    productManufacturerName={item.productManufacturerName}
                                    productAttributeValue={item.productAttributeValue}
                                    productAttributeKey={item.productAttributeKey}
                                    packageType={item.packageType}
                                    stockQuantity={item.stockQuantity}
                                    orderQuantity={item.orderQuantity}
                                    description={item.description || item.productDescription || item.shortDescription || item.productShortDescription}
                                    categoryName={item.categoryName}
                                    imageSrc={imageSrc}
                                    CustomRouter={CustomRouter}
                                />
                            );
                        })()
                    ))}
                    <li className="search-options__footer">
                        <span className="search-options__note">Показаны первые {hintsList.length} результата поиска</span>
                        <button
                            onClick={async () => {
                                setIsActive(false);
                                try {
                                    await hintsService.saveSearchHistory(currentSearchValue);
                                    await hintsService.storeSearchQuery(currentSearchValue);
                                    window.location.href = `/search?q=${currentSearchValue}&offset=0&limit=5`;
                                } catch (error) {
                                    console.error("Ошибка при обработке запроса:", error);
                                }
                            }}
                            className="search-options__show-all-button"
                        >
                            Показать все результаты
                        </button>
                    </li>
                </>
            )}
        </ul>
    );
};

export default HintsList;
